import { LitElement, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import Styles from './vm-clamped-text.scss';
import { classMap } from 'lit/directives/class-map.js';

/**
 * Clamps slotted content to a fixed number of lines and reveals a
 * "...read more" button when the content overflows. Clicking the
 * button expands the box to show all content.
 *
 * The fade gradient behind the button defaults to transparent — set
 * `text-background` to match the surrounding background.
 *
 * @slot - Content to clamp. Plain text or inline HTML.
 *
 * @example
 * ```html
 * <vm-clamped-text lines="3" text-background="#eff0f0">
 *   Long description text...
 * </vm-clamped-text>
 * ```
 */
@customElement('vm-clamped-text')
export class VmClampedText extends LitElement {
    static override styles = Styles;

    @property({ type: Number }) lines = 3;
    @property({ type: String, attribute: 'expand-label' }) expandLabel = '...read more';
    @property({ type: String, attribute: 'text-background' }) textBackground = '';

    @state() private _expanded = false;
    @state() private _overflows = false;

    @query('.vm-clamped-text__body') private _bodyEl?: HTMLElement;

    private _initialCheckFrame: number | null = null;

    private _resizeObserver?: ResizeObserver;

    private _checkOverflow = () => {
        const el = this._bodyEl;
        if (!el || this._expanded) return;
        this._overflows = el.scrollHeight > el.clientHeight + 1;
    };

    private _onSlotChange = () => {
        this._expanded = false;
        this._checkOverflow();
    };

    private _expand = () => {
        this._expanded = true;
    };

    protected override willUpdate(changed: Map<string, unknown>) {
        if (changed.has('lines')) {
            this.style.setProperty('--vm-clamped-text-lines', String(this.lines));
        }
        if (changed.has('textBackground')) {
            this.style.setProperty('--vm-clamped-text-bg', this.textBackground || '');
        }
    }

    protected override firstUpdated() {
        if (this._bodyEl) {
            this._resizeObserver = new ResizeObserver(this._checkOverflow);
            this._resizeObserver.observe(this._bodyEl);
        }
        // Defer to the next frame so layout has settled — otherwise an initial
        // measurement inside a just-revealed container (e.g. a bottom sheet
        // transitioning from display: none) can catch the text mid-wrap and
        // stick `_overflows` to true.
        this._initialCheckFrame = requestAnimationFrame(() => {
            this._initialCheckFrame = null;
            this._checkOverflow();
        });
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this._resizeObserver?.disconnect();
        this._resizeObserver = undefined;
        if (this._initialCheckFrame !== null) cancelAnimationFrame(this._initialCheckFrame);
    }

    override render() {
        return html`
            <div
                class=${classMap({ 'vm-clamped-text__body': true, 'vm-clamped-text__body--expanded': this._expanded })}
            >
                <slot @slotchange=${this._onSlotChange}></slot>
            </div>
            ${!this._expanded && this._overflows
                ? html`<button type="button" class="vm-clamped-text__read-more" @click=${this._expand}>
                      ${this.expandLabel}
                  </button>`
                : nothing}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-clamped-text': VmClampedText;
    }
}
